import { useState, useEffect } from 'react';
import { Plus, Folder, Trash2, AlertCircle, RefreshCw, Database } from 'lucide-react';
import { Button } from '@lean-agility/design-system/react';
import { Input } from '@lean-agility/design-system/react';
import { Select, type SelectOption } from '@lean-agility/design-system/react';
import { Card, CardHeader, CardTitle, CardContent } from '@lean-agility/design-system/react';
import { Spinner } from '@lean-agility/design-system/react';
import { IconContainer } from '@/components/atoms';
import { listSimulations, createSimulation, deleteSimulation, checkDatabaseHealthy } from '@/lib/api';
import type { SimulationSummary, ScenarioSize } from '@/types/api';
import { formatDistanceToNow } from 'date-fns';
import { useErrorHandler } from '@/hooks';

const SCENARIO_SIZE_OPTIONS: (SelectOption & { value: ScenarioSize })[] = [
  { value: 'empty', label: 'Empty', description: 'Start with a blank simulation' },
  { value: 'small', label: 'Small', description: '~15 deliverables across 3 teams' },
  { value: 'medium', label: 'Medium', description: '~100 deliverables across 10 teams' },
  { value: 'large', label: 'Large', description: '~450 deliverables across 20 teams' },
];

interface SimulationManagerProps {
  onSelectSimulation: (simulation: SimulationSummary) => void;
}

export function SimulationManager({ onSelectSimulation }: SimulationManagerProps) {
  const { handleError } = useErrorHandler('Simulations');
  const [simulations, setSimulations] = useState<SimulationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDatabaseHealthy, setIsDatabaseHealthy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newSimName, setNewSimName] = useState('');
  const [newSimDescription, setNewSimDescription] = useState('');
  const [scenarioSize, setScenarioSize] = useState<ScenarioSize>('empty');
  const [isCreating, setIsCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Fetch simulations. `isLoading` is already `true` on the initial mount (via
  // useState(true)), so the mount path needs no synchronous pre-await setState.
  const loadSimulations = async () => {
    try {
      // First check if database is healthy
      const available = await checkDatabaseHealthy();
      setIsDatabaseHealthy(available);

      if (available) {
        const sims = await listSimulations();
        setSimulations(sims);
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load simulations');
    } finally {
      setIsLoading(false);
    }
  };

  // Retry entry point for the manual refresh / reconnect buttons: reset the
  // loading + error state synchronously (outside the mount path) before
  // re-fetching.
  const reloadSimulations = async () => {
    setIsLoading(true);
    setLoadError(null);
    await loadSimulations();
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const available = await checkDatabaseHealthy();
        if (cancelled) return;
        setIsDatabaseHealthy(available);
        if (available) {
          const sims = await listSimulations();
          if (cancelled) return;
          setSimulations(sims);
        }
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'Failed to load simulations');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCreateSimulation = async () => {
    if (!newSimName.trim()) return;

    setIsCreating(true);

    try {
      const today = new Date().toISOString().slice(0, 10);
      const newSim = await createSimulation({
        name: newSimName.trim(),
        description: newSimDescription.trim() || null,
        start_date: today,
        seed: null,
        scenario_size: scenarioSize,
      });

      setSimulations((prev) => [newSim, ...prev]);
      setShowCreateDialog(false);
      setNewSimName('');
      setNewSimDescription('');
      setScenarioSize('empty');

      // Automatically select the new simulation
      onSelectSimulation(newSim);
    } catch (err) {
      handleError(err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteSimulation = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!confirm('Are you sure you want to delete this simulation? This action cannot be undone.')) {
      return;
    }

    setDeletingId(id);

    try {
      await deleteSimulation(id);
      setSimulations((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      handleError(err);
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isDatabaseHealthy) {
    return (
      <div className="mx-auto max-w-2xl">
        <Card>
          <CardContent className="py-12 text-center">
            <IconContainer variant="muted" size="xl" className="mx-auto mb-4 rounded-full">
              <Database className="size-8 text-text-subtle" />
            </IconContainer>
            <h3 className="text-theme-primary mb-2 text-lg font-semibold">Database Not Available</h3>
            <p className="text-theme-muted mx-auto mb-6 max-w-md">
              This application requires a PostgreSQL database connection to store simulations. Please ensure the
              database is running and configured correctly.
            </p>
            <Button variant="secondary" onClick={reloadSimulations}>
              <RefreshCw className="mr-2 size-4" />
              Retry Connection
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-theme-primary text-2xl font-bold">Simulations</h2>
          <p className="text-theme-muted">Manage your saved simulation scenarios</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={reloadSimulations}>
            <RefreshCw className="size-4" />
          </Button>
          <Button variant="primary" onClick={() => setShowCreateDialog(true)}>
            <Plus className="mr-2 size-4" />
            New Simulation
          </Button>
        </div>
      </div>

      {/* Error Display - for loading errors only */}
      {loadError && (
        <div className="rounded-lg border border-accent-rose/30 bg-feedback-error/10 p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="size-5 text-feedback-error" />
            <p className="text-sm text-feedback-error">{loadError}</p>
          </div>
        </div>
      )}

      {/* Create Dialog */}
      {showCreateDialog && (
        <Card>
          <CardHeader>
            <CardTitle>Create New Simulation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Simulation Name"
              placeholder="e.g., Q1 2025 Plan"
              value={newSimName}
              onChange={(e) => setNewSimName(e.target.value)}
              autoFocus
            />
            <Input
              label="Description (optional)"
              placeholder="Brief description of this scenario"
              value={newSimDescription}
              onChange={(e) => setNewSimDescription(e.target.value)}
            />
            <Select
              label="Initial Data"
              value={scenarioSize}
              onChange={(value) => setScenarioSize(value as ScenarioSize)}
              options={SCENARIO_SIZE_OPTIONS}
              hint="Choose the size of the example scenario to generate"
            />
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowCreateDialog(false);
                  setNewSimName('');
                  setNewSimDescription('');
                  setScenarioSize('empty');
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={handleCreateSimulation} disabled={!newSimName.trim() || isCreating}>
                {isCreating ? (
                  <>
                    <Spinner size="sm" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="mr-1 size-4" />
                    Create
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Simulations List */}
      {simulations.length === 0 ? (
        <Card elevation="soft">
          <CardContent className="py-12 text-center">
            <IconContainer variant="muted" size="xl" className="mx-auto mb-4 rounded-full">
              <Folder className="size-8 text-text-subtle" />
            </IconContainer>
            <h3 className="text-theme-primary mb-2 text-lg font-semibold">No Simulations Yet</h3>
            <p className="text-theme-muted mb-6">
              Create your first simulation to start forecasting delivery timelines.
            </p>
            <Button variant="primary" onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 size-4" />
              Create Simulation
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {simulations.map((sim) => (
            <Card key={sim.id} interactive elevation="soft" onClick={() => onSelectSimulation(sim)}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <h3
                      className="
                        truncate text-xl font-semibold text-text-primary
                        dark:text-text-primary
                      "
                    >
                      {sim.name}
                    </h3>
                    {sim.description && (
                      <p className="mt-1 line-clamp-2 text-sm text-text-secondary">{sim.description}</p>
                    )}
                    <div className="mt-2 flex items-center gap-4 text-xs text-text-muted">
                      <span>Start: {sim.start_date}</span>
                      <span>Created {formatDistanceToNow(new Date(sim.created_at), { addSuffix: true })}</span>
                      {sim.seed && <span>Seed: {sim.seed}</span>}
                    </div>
                  </div>
                  <div className="ml-4 flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleDeleteSimulation(sim.id, e)}
                      disabled={deletingId === sim.id}
                      className="
                        text-text-subtle
                        hover:bg-feedback-error/10 hover:text-feedback-error!
                      "
                    >
                      {deletingId === sim.id ? <Spinner size="sm" /> : <Trash2 className="size-4" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
